import { create } from 'zustand';
import { supabase, type Profile, type UserSettings } from '../lib/supabase';

interface UserState {
  profile: Profile | null;
  settings: UserSettings | null;
  isLoading: boolean;
  error: Error | null;
  fetchProfile: () => Promise<void>;
  fetchSettings: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  updateSettings: (updates: Partial<UserSettings>) => Promise<void>;
}

export const useUserStore = create<UserState>((set, get) => ({
  profile: null,
  settings: null,
  isLoading: false,
  error: null,

  fetchProfile: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      set({ profile: data });

      // After fetching profile, also fetch settings
      get().fetchSettings();
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchSettings: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user logged in');

      // First try to get existing settings
      const { data: existingSettings, error: fetchError } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingSettings) {
        set({ settings: existingSettings });
        return;
      }

      // If no settings exist, create them with defaults
      const { data: newSettings, error: createError } = await supabase
        .from('user_settings')
        .insert([{
          user_id: user.id,
          cache_duration: '1 hour',
          realtime_enabled: true,
          sync_frequency: '5 minutes',
          offline_mode: false
        }])
        .select()
        .single();

      if (createError) throw createError;
      set({ settings: newSettings });
    } catch (error) {
      console.error('Error handling settings:', error);
      // Don't set error state for settings issues
    }
  },

  updateProfile: async (updates) => {
    try {
      set({ isLoading: true, error: null });
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      set({ profile: data });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ isLoading: false });
    }
  },

  updateSettings: async (updates) => {
    try {
      set({ isLoading: true, error: null });
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase
        .from('user_settings')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      set({ settings: data });
    } catch (error) {
      console.error('Error updating settings:', error);
      // Don't set error state for settings issues
    } finally {
      set({ isLoading: false });
    }
  },
}));